import React from 'react';
import { BsThreeDots } from 'react-icons/bs';
import Link from 'components/Link';
import { useTranslation } from 'react-i18next';

import { useGetOrganizationMembers } from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import { GroupMemberDto, GroupMemberRoleEnum } from 'legacy/groups/types';
import { Avatar, AvatarBadge } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

interface Props {
  organizationId: string;
  search: string;
  setSelectedMember: (member: GroupMemberDto) => void;
  isUserAdmin?: boolean;
}

const AdminsList = ({
  organizationId,
  search,
  setSelectedMember,
  isUserAdmin
}: Props) => {
  const { t } = useTranslation(['organizations']);
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetOrganizationMembers({
    organizationId,
    role: `${GroupMemberRoleEnum.ADMIN},${GroupMemberRoleEnum.OWNER}`,
    search
  });
  const flag = useFlags();

  const onSelectMember = (member: GroupMemberDto) => () =>
    setSelectedMember(member);

  return (
    <MemberList
      title={t('members.adminsTitle')}
      description={t('members.adminsDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      headerContent={
        <>
          {isUserAdmin && flag[FLAGS.ENABLE_ORGANIZATION_TITLES] && (
            <Link href={`/organizations/${organizationId}/members/titles`}>
              <div className="mt-3 h-11 px-4 flex justify-center items-center rounded-full bg-primary-200 text-primary-700">
                <img
                  src="/img/org/shield_person.svg"
                  className="w-6 h-6 mr-2"
                  alt="Shield Person"
                />
                {t('members.manageExecutiveTitles')}{' '}
              </div>
            </Link>
          )}
        </>
      }
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((admin) => (
          <MembersListItem
            key={admin.id}
            avatar={
              <Avatar src={admin.user.photoUrl}>
                <AvatarBadge>
                  <img src="/img/icons/shield-person-gradient.svg" />
                </AvatarBadge>
              </Avatar>
            }
            label={`${admin.user.firstName} ${admin.user.lastName}`}
            title={`${admin.titles ? admin.titles.join(', ') : ''}`}
            actions={
              isUserAdmin && (
                <button
                  type="button"
                  aria-label={t('members.memberActions')}
                  onClick={onSelectMember(admin)}
                >
                  <BsThreeDots size={20} />
                </button>
              )
            }
          />
        ))
      )}
    </MemberList>
  );
};

export default AdminsList;
